/** Auto Sourcing API client — browser talks only to the Ezisell API.
 *
 * Uses the SAME authenticated HTTP helpers exported from @/lib/api
 * (get/post/patch/del) so requests carry the stored Bearer token and resolve
 * against the configured API base — no duplicate axios client, no double-prefixed
 * API path.
 */
import { get, post, patch, del, unwrapEnvelope } from "@/lib/api";

async function jsonRequest(url, options = {}) {
  const method = (options.method || "GET").toLowerCase();
  let res;
  if (method === "post") {
    res = await post(url, options.body ?? {});
  } else if (method === "patch") {
    res = await patch(url, options.body ?? {});
  } else if (method === "delete") {
    res = await del(url);
  } else {
    res = await get(url);
  }
  return unwrapEnvelope(res);
}

/** Normalize a source row to always carry `source_id` (backend registry uses
 * `id` internally; the UI serializer emits `source_id`, but normalize here so
 * the picker never selects on an undefined id). */
function normalizeSource(row) {
  if (!row || typeof row !== "object") return row;
  if (!row.source_id && row.id) {
    return { ...row, source_id: String(row.id) };
  }
  if (row.source_id) {
    return { ...row, source_id: String(row.source_id) };
  }
  return row;
}

export async function listSourcingSources({ knownVersion } = {}) {
  // Returns the full source catalog: { items, total, operational_count, version },
  // or { unchanged: true, version } when `knownVersion` is still current.
  const suffix = knownVersion ? `?known_version=${encodeURIComponent(knownVersion)}` : "";
  const data = await jsonRequest(`/product-sourcing/sources${suffix}`);
  if (data?.unchanged) return { unchanged: true, version: data.version ?? null };
  const items = Array.isArray(data?.items)
    ? data.items.map(normalizeSource).filter((s) => s && s.status !== "excluded")
    : [];
  return {
    items,
    total: items.length,
    operational_count: data?.operational_count ?? 0,
    version: data?.version ?? null,
  };
}

export async function getAutoSourcingCapabilities() {
  // { profile_sections: { <section>: "enforced" | "stored_not_enforced" } }
  return jsonRequest("/product-sourcing/capabilities");
}

export async function listSourceCategories(sourceId) {
  // Persisted category catalog for a source (DB read only, 0 provider calls).
  return jsonRequest(`/product-sourcing/sources/${encodeURIComponent(sourceId)}/categories`);
}

export async function listManualCategories(sourceId) {
  // This workspace's saved category URLs for a retailer + their verification state (DB read only).
  return jsonRequest(`/product-sourcing/sources/${encodeURIComponent(sourceId)}/manual-categories`);
}

export async function getAutoSourcingRun(runId) {
  return jsonRequest(`/product-sourcing/runs/${encodeURIComponent(runId)}`);
}

/** Server-paginated durable run products (50 / 100 / 200). */
export async function listAutoSourcingRunProducts(runId, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.set(key, String(value));
    }
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return jsonRequest(
    `/product-sourcing/runs/${encodeURIComponent(runId)}/products${suffix}`,
  );
}

/** Stage G acceptance metrics (confirmed WINNING only). */
export async function getAutoSourcingAcceptance(runId) {
  return jsonRequest(
    `/product-sourcing/runs/${encodeURIComponent(runId)}/acceptance`,
  );
}

/** CSV download URL for durable Results (default WINNING). */
export function autoSourcingProductsCsvUrl(runId, bucket = "qualified") {
  const qs = new URLSearchParams();
  if (bucket) qs.set("bucket", bucket);
  return `/api/v1/product-sourcing/runs/${encodeURIComponent(runId)}/products.csv?${qs}`;
}

export async function listAutoSourcingResults(params = {}) {
  // Opportunities filtered by profile/run provenance (canonical endpoint).
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.set(key, String(value));
    }
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return jsonRequest(`/product-sourcing/opportunities${suffix}`);
}

export async function listAutoSourcingProfiles() {
  return jsonRequest("/product-sourcing/profiles");
}

export async function createAutoSourcingProfile(payload) {
  return jsonRequest("/product-sourcing/profiles", {
    method: "POST",
    body: payload,
  });
}

export async function updateAutoSourcingProfile(id, payload) {
  return jsonRequest(`/product-sourcing/profiles/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: payload,
  });
}

export async function cloneAutoSourcingProfile(id) {
  return jsonRequest(`/product-sourcing/profiles/${encodeURIComponent(id)}/clone`, {
    method: "POST",
  });
}

export async function deleteAutoSourcingProfile(id) {
  return jsonRequest(`/product-sourcing/profiles/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function runAutoSourcingProfile(id) {
  return jsonRequest(`/product-sourcing/profiles/${encodeURIComponent(id)}/run`, {
    method: "POST",
  });
}

/** Resume a recoverable interrupted run (backend-gated via resume_available). */
export async function resumeAutoSourcingRun(runId) {
  return jsonRequest(`/product-sourcing/runs/${encodeURIComponent(runId)}/resume`, {
    method: "POST",
  });
}

/** Pause a running scan (frozen duration, preserved budget/checkpoints). */
export async function pauseAutoSourcingRun(runId) {
  return jsonRequest(`/product-sourcing/runs/${encodeURIComponent(runId)}/pause`, {
    method: "POST",
  });
}

/** Stop (cancel) a queued, running or paused scan and release its slot. */
export async function stopAutoSourcingRun(runId) {
  return jsonRequest(`/product-sourcing/runs/${encodeURIComponent(runId)}/stop`, {
    method: "POST",
  });
}

export async function listAutoSourcingRuns(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.set(key, String(value));
    }
  });
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return jsonRequest(`/product-sourcing/runs${suffix}`);
}
